import type { ModeDefinition } from "@picnic-field/local-host/protocol";
import { Button } from "../../../components/ui/Button";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import type { Translate } from "../../../i18n";
import type { Mission, MissionGoal } from "../../../lib/api/missionApi";
import { formatClock } from "../lib/progress";
import { presetTitle } from "../lib/presets";
import type { MissionRunView } from "../types";

type Preset = ModeDefinition["presets"][number];

/** 목표 시간 '30초' · '30.5초'(소수 한 자리까지) */
export const goalSeconds = (ms: number) => String(Math.round(ms / 100) / 10);

/** 목표 한 줄 — 저작 제목이 없으면 '코스 이름(· n초 안에)' */
export function goalText(t: Translate, g: MissionGoal, presets: readonly Preset[]): string {
  const p = presets.find((x) => x.key === g.presetKey);
  const course = p ? presetTitle(t, p) : g.presetKey;
  const time =
    g.timeUnderMs != null ? t("course.mission.within", { s: goalSeconds(g.timeUnderMs) }) : "";
  return [course, time].filter(Boolean).join(" · ");
}

const STATE_CHIP: Record<MissionGoal["state"], { key: string; tone: string } | null> = {
  open: null,
  pending: { key: "course.mission.state.pending", tone: "info" },
  verified: { key: "course.mission.state.verified", tone: "ok" },
  failed: { key: "course.mission.state.failed", tone: "run" },
};

/**
 * FS-04-01 '오늘의 미션' 카드(4.6 §4.3 · SCR v0.7 정정) — Campus 미션 제목 · 목표 코스 · 시간. 누르면 그 코스를 고른다.
 * 다른 코스도 달릴 수 있지만 미션은 그 코스에서만 센다(안내 한 줄). 남의 기록 · 순위 없음(DR-01).
 */
export function MissionCard({
  mission,
  modeId,
  presets,
  presetKey,
  onPreset,
  hideNote = false,
}: {
  mission: Mission;
  modeId: string;
  presets: readonly Preset[];
  presetKey: string;
  onPreset: (k: string) => void;
  /** 미션 고르기 대화상자 안(2026-10-09) — '다른 코스' 안내는 오른쪽 열 미션 줄(MissionSummary)이 한다 */
  hideNote?: boolean;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const goals = mission.goals.filter((g) => g.modeId === modeId);
  if (!goals.length) return null;
  const here = goals.some((g) => g.presetKey === presetKey);
  const firstOpen = goals.find((g) => g.state !== "verified") ?? goals[0]!;
  const target = presets.find((p) => p.key === firstOpen.presetKey);
  return (
    <section
      className={`pf-pane pf-mission pf-mission--${density}`}
      data-testid="mission-card"
      aria-labelledby="mission-card-title"
    >
      <h2 id="mission-card-title" className="pf-mission__title">
        <span aria-hidden="true">🎯 </span>
        {t("course.mission.title")}
      </h2>
      <ul className="pf-mission__goals">
        {goals.map((g) => {
          const chip = STATE_CHIP[g.state];
          const on = g.presetKey === presetKey;
          return (
            <li key={g.key}>
              <Button
                variant={on ? "accent" : "ghost"}
                className="pf-mission__goal"
                data-testid={`mission-goal-${g.key}`}
                data-state={g.state}
                data-preset={g.presetKey}
                aria-pressed={on}
                onClick={() => onPreset(g.presetKey)}
              >
                <span className="pf-mission__goal-title">{g.title || goalText(t, g, presets)}</span>
                {g.title && <span className="pf-mission__goal-sub">{goalText(t, g, presets)}</span>}
                {chip && (
                  <span className={`pf-chip-static pf-chip-static--${chip.tone}`}>
                    {t(chip.key)}
                    {g.state === "verified" && g.timeMs != null && !se
                      ? ` · ${formatClock(g.timeMs / 1000, true)}`
                      : ""}
                  </span>
                )}
              </Button>
            </li>
          );
        })}
      </ul>
      {!hideNote && !here && target && (
        <p className="pf-muted pf-mission__note" data-testid="mission-other-preset">
          {t("course.mission.onlyThere", { course: presetTitle(t, target) })}
        </p>
      )}
    </section>
  );
}

/**
 * 오른쪽 열 '오늘의 미션' 한 줄(2026-10-09 — 미션 고르기와 코스 고르기를 나눴다) — 지금 코스의 미션 목표(없으면 아직 안 한 첫 목표) · 상태 칩 ·
 * [미션 고르기](미션 고르기 대화상자). 지금 코스가 미션 코스가 아니면 '미션은 X 코스에서만' 안내.
 */
export function MissionSummary({
  mission,
  modeId,
  presets,
  presetKey,
  open,
  onOpen,
}: {
  mission: Mission;
  modeId: string;
  presets: readonly Preset[];
  presetKey: string;
  open: boolean;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const goals = mission.goals.filter((g) => g.modeId === modeId);
  if (!goals.length) return null;
  const here = goals.some((g) => g.presetKey === presetKey);
  const firstOpen = goals.find((g) => g.state !== "verified") ?? goals[0]!;
  const cur = goals.find((g) => g.presetKey === presetKey) ?? firstOpen;
  const target = presets.find((p) => p.key === firstOpen.presetKey);
  const chip = STATE_CHIP[cur.state];
  const sub = goalText(t, cur, presets);
  return (
    <section
      className={`pf-pane pf-mission pf-mission--${density} pf-mission--summary`}
      data-testid="mission-summary"
      data-goal={cur.key}
      aria-labelledby="mission-summary-title"
    >
      <h2 id="mission-summary-title" className="pf-mission__title">
        <span aria-hidden="true">🎯 </span>
        {t("course.mission.title")}
      </h2>
      <div className="pf-course__current">
        <span className="pf-course__current-name">
          {cur.title || sub}
          {cur.title && <span className="pf-course__current-sub"> · {sub}</span>}
        </span>
        {chip && (
          <span className={`pf-chip-static pf-chip-static--${chip.tone}`}>{t(chip.key)}</span>
        )}
        <Button
          variant="accent"
          className="pf-course__change"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={onOpen}
          data-testid="mission-pick-open"
        >
          {t("course.mission.pick")}
        </Button>
      </div>
      {!here && target && (
        <p className="pf-muted pf-mission__note" data-testid="mission-other-preset">
          {t("course.mission.onlyThere", { course: presetTitle(t, target) })}
        </p>
      )}
    </section>
  );
}

const BAND: Record<Exclude<MissionRunView["state"], "none">, { tone: string; icon: string }> = {
  saving: { tone: "info", icon: "🎯" },
  pending: { tone: "info", icon: "🎯" },
  verified: { tone: "ok", icon: "🎯" },
  failed: { tone: "warn", icon: "🙋" },
  unsaved: { tone: "warn", icon: "💾" },
  over: { tone: "info", icon: "⏱" },
  already: { tone: "ok", icon: "🎯" },
};

/**
 * FS-04-03 미션 띠 — '미션 달성 · 확인 중' → '확인됨 ✓' · '확인하지 못했어요 — 선생님께'(4.6 §4.3 ③ · Q8 기다린다).
 * 시간 밖이면 본인 시간 대 목표(Q12 — 본인만) + 규칙 힌트 한 줄(SR-ACH-04 어휘 — 탓하지 않는다).
 */
export function MissionBand({ view }: { view: MissionRunView }) {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  if (view.state === "none") return null;
  const b = BAND[view.state];
  const titles = view.goals.map((g) => g.title).filter(Boolean);
  const text =
    view.state === "over" && view.goalMs != null && view.timeMs != null
      ? t(`course.mission.band.over.${density}`, {
          goal: formatClock(view.goalMs / 1000, !se),
          time: formatClock(view.timeMs / 1000, !se),
        })
      : t(`course.mission.band.${view.state}.${density}`);
  return (
    <div
      className={`pf-banner pf-banner--${b.tone} pf-mission__band`}
      role="status"
      aria-live="polite"
      data-testid="mission-band"
      data-state={view.state}
    >
      <p className="pf-banner__text">
        <span aria-hidden="true">{b.icon} </span>
        {text}
        {titles.length > 0 && view.state !== "over" && (
          <span className="pf-mission__band-goals"> — {titles.join(" · ")}</span>
        )}
      </p>
      {view.slow && view.state === "pending" && (
        <p className="pf-muted pf-mission__band-note" data-testid="mission-band-slow">
          {t("course.mission.band.slow")}
        </p>
      )}
    </div>
  );
}
